import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ProjectHeader from '@/components/header';
import { Card, CardContent, CardTitle, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Trash2 } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { ProjectService } from '@/services/ProjectService';
import { FaSearch, FaExternalLinkAlt } from 'react-icons/fa';
import { ReactComponent as Other } from '@/components/icons/other-new.svg';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import axios from 'axios';
import ApplianceOptionsDialog from '../components/appliancesComponents/ApplianceOptionsDialog';
import CollectionNavigation from '@/components/CollectionNavigation';
import ApplianceDeleteDialog from '../components/appliancesComponents/ApplianceDeleteDialog';
import { useCollectionData } from '@/hooks/useCollectionData';
import { AssetsConfig } from '@/services/AssetsConfig';


const API_BASE_URL =
    process.env.REACT_SEARCH_APP_API_BASE_URL || 'https://xppqnn7pn6.execute-api.us-east-1.amazonaws.com/default';
const api = axios.create({
    baseURL: API_BASE_URL,
});

const ApplianceSelector = ({ flowId, onComplete }) => {
    const [selectedAppliances, setSelectedAppliances] = useState([]);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [currentAppliance, setCurrentAppliance] = useState(null);
    const [sizeType, setSizeType] = useState('');
    const [customSize, setCustomSize] = useState({ width: '', depth: '', height: '', additionalInfo: '' });
    const [searchProduct, setSearchProduct] = useState('');
    const [customName, setCustomName] = useState('');
    const [standardSize, setStandardSize] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();
    const { projectId } = useParams();
    const [allAppliances, setAllAppliances] = useState({});
    const [newAppliancesOther, setNewAppliancesOther] = useState(false);
    const [otherParametrs, setOtherParametrs] = useState(false);
    const [isOptionsDialogOpen, setIsOptionsDialogOpen] = useState(false);
    const [selectedApplianceForOptions, setSelectedApplianceForOptions] = useState(null);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [categoryToDelete, setCategoryToDelete] = useState(null);
    const [applianceType, setApplianceType] = useState('');
    const [projectData, setProjectData] = useState(null);
    const [isPanelReady, setIsPanelReady] = useState(false);
    const { data: collectionData, iscollectionLoading } = useCollectionData(projectData?.collectionId);
    const [applianceConfig, setApplianceConfig] = useState({
        appliances: [],
        standardSizes: {},
        applianceTypes: {}
      });

    useEffect(() => {
        async function fetchAppliances() {
            setIsLoading(true);
            try {
                const currentProject = await ProjectService.getProjectById(projectId);
                setProjectData(currentProject);

                // Load appliance configuration from S3
                const config = await AssetsConfig.getAppliances(null, currentProject?.type || 'kitchen');
                setApplianceConfig(config);
                setAllAppliances(config.appliances);
                // console.log('config:', config);

                if (currentProject.appliances) {
                    let savedAppliances = currentProject.appliances;

                    if (typeof currentProject.appliances === 'string') {
                        try {
                            savedAppliances = JSON.parse(currentProject.appliances);
                        } catch (parseError) {
                            console.error('Error parsing appliances string:', parseError);
                        }
                    }

                    // Map the saved appliances to include the icon URL
                    const appliancesWithIcons = savedAppliances.map(appliance => {
                        const baseId = appliance.id.split('-')[0];
                        const applianceTemplate = config.appliances.find(a => a.id === baseId);
                        
                        return {
                            ...appliance,
                            iconUrl: applianceTemplate?.iconUrl || config.appliances.find(a => a.id === 'other')?.iconUrl
                        };
                    });
                    // console.log('appliancesWithIcons:', appliancesWithIcons);

                    setSelectedAppliances(appliancesWithIcons);
                }
            } catch (error) {
                console.error('Error in fetchAppliances:', error);
            } finally {
                setIsLoading(false);
            }
        }
        fetchAppliances();
    }, [projectId]);

    useEffect(() => {
        const DELAY_TIME = 5000;
        let timeoutId;

        const resetTimeout = () => {
            clearTimeout(timeoutId); // Clear any existing timeout
            timeoutId = setTimeout(() => {
                saveAppliances(); // Save appliances if no click for 5 seconds
            }, DELAY_TIME);
        };

        if (selectedAppliances.length > 0) {
            document.addEventListener('click', resetTimeout);

            // Call resetTimeout initially to set the timeout
            resetTimeout();

            // Cleanup event listener and timeout on unmount
            return () => {
                document.removeEventListener('click', resetTimeout);
                clearTimeout(timeoutId);
            };
        }
    }, [selectedAppliances, allAppliances]);

    const saveAppliances = async () => {
        try {
            const appliancesToSave = selectedAppliances.map(appliance => ({
                id: appliance.id,
                name: appliance.name,
                sizeInfo: appliance.sizeInfo,
                dimensions: {
                    width: appliance.dimensions.width.toString(),
                    depth: appliance.dimensions.depth.toString(),
                    height: appliance.dimensions.height.toString(),
                },
                additionalInfo: appliance.additionalInfo,
                sizeType: appliance.sizeType,
                productId: appliance.productId,
                panel_Ready: appliance.panelReady,
            }));

            // console.log('appliancesToSave:', appliancesToSave);
            const response = await ProjectService.updateProject(projectId, {
                appliances: appliancesToSave,
            });
        } catch (error) {
            console.error('Failed to save appliances:', error);
            // console.log('Error details:', {
            //     message: error.message,
            //     stack: error.stack,
            // });
        }
    };

    useEffect(() => {
        if (sizeType === 'standard' && !otherParametrs && currentAppliance?.id) {
            // const sizes = applianceConfig.standardSizes[currentAppliance.id];
            const sizes = applianceConfig.appliances.find(app => app.id === currentAppliance.id)?.standardSizes || [];
            if (sizes && sizes.length > 0) {
                setStandardSize(JSON.stringify(sizes[0]));
            }
        }
    }, [sizeType, otherParametrs, currentAppliance, applianceConfig]);

    const handleComplete = () => {
        saveAppliances();
        if (flowId) {
            async function fetchCompletedFlow() {
                await ProjectService.addCompletedFlow(projectId, flowId);
                navigate(`/project/${projectId}`);
            }
            fetchCompletedFlow();
        } else if (onComplete) {
            onComplete();
        }
    };

    const handleChangeSize = e => {
        setStandardSize(e.target.value);
    };

    const handleApplianceClick = appliance => {
        const existingAppliance = selectedAppliances.find(
            a => a.id === appliance.id || a.id.startsWith(`${appliance.id}-`)
        );

        if (existingAppliance) {
            setSelectedApplianceForOptions(existingAppliance);
            setIsOptionsDialogOpen(true);
        } else {
            openDialog(appliance);
        }
    };

    const openDialog = appliance => {
        setCurrentAppliance(appliance);

        if (appliance.id === 'other') {
            setCurrentAppliance(null);
            setOtherParametrs(true);
            setNewAppliancesOther(true);
            setSizeType('');
            setIsDialogOpen(true);
        } else {
            setNewAppliancesOther(false);
            const existingAppliance = selectedAppliances.find(a => a.id === appliance.id);
            setOtherParametrs(false);
            if (existingAppliance) {
                setSizeType(existingAppliance.sizeType || '');
                setApplianceType(existingAppliance.applianceType || '');
                setIsPanelReady(existingAppliance.panelReady || false);
                if (existingAppliance.dimensions) {
                    const dimensionStr = JSON.stringify(existingAppliance.dimensions);
                    setStandardSize(dimensionStr);
                    setCustomSize({
                        width: existingAppliance.dimensions.width.toString(),
                        depth: existingAppliance.dimensions.depth.toString(),
                        height: existingAppliance.dimensions.height.toString(),
                        additionalInfo: existingAppliance.additionalInfo || '',
                    });
                }
            } else {
                setSizeType('standard');
                setCustomSize({ width: '', depth: '', height: '', additionalInfo: '' });
                setApplianceType('');
                setIsPanelReady(false);
            }
            setSearchProduct('');
            setSearchResults([]);
            setIsDialogOpen(true);
        }
    };

    const handleDeleteFromOptions = () => {
        if (selectedApplianceForOptions) {
            const baseId = selectedApplianceForOptions.id.split('-')[0];
            const categoryAppliances = selectedAppliances.filter(a => a.id === baseId || a.id.startsWith(`${baseId}-`));

            if (categoryAppliances.length > 1) {
                setCategoryToDelete({
                    name: selectedApplianceForOptions.name,
                    baseId: baseId,
                    appliances: categoryAppliances,
                });
                setIsDeleteDialogOpen(true);
                setIsOptionsDialogOpen(false);
            } else {
                handleRemoveAppliance(selectedApplianceForOptions.id);
                setIsOptionsDialogOpen(false);
            }
            setSelectedApplianceForOptions(null);
        }
    };

    const handleAddAnotherFromOptions = () => {
        if (selectedApplianceForOptions) {
            const baseId = selectedApplianceForOptions.id.split('-')[0];
            const applianceTemplate = allAppliances.find(a => a.id === baseId);

            const isCustomAppliance = !applianceConfig.appliances.find(a => a.id === baseId);

            if (isCustomAppliance) {
                setOtherParametrs(true);
            }

            openDialog(applianceTemplate);
            setIsOptionsDialogOpen(false);
            setSelectedApplianceForOptions(null);
        }
    };

    const generateUniqueId = baseId => {
        const existingCount = selectedAppliances.filter(a => a.id.startsWith(baseId)).length;
        return `${baseId}-${existingCount + 1}`;
    };

    const handleOtherApplianceSave = () => {
        const newAppliance = {
            id: 'other_' + customName.toLowerCase().replace(/\s+/g, '-'),
            name: customName,
            icon: Other,
        };

        setCurrentAppliance(newAppliance);
        setNewAppliancesOther(false);
        setCustomName('');
    };

    const handleSaveAppliance = (result = null) => {
        setOtherParametrs(false);
        let dimensions;
        let appliance;

        const uniqueId =
            currentAppliance.id === selectedApplianceForOptions?.id
                ? selectedApplianceForOptions.id
                : generateUniqueId(currentAppliance.id);

        // Create the merged name for sink and microwave
        let mergedName = currentAppliance.name;
        if ((currentAppliance.id === 'sink' || currentAppliance.id === 'microwave') && applianceType) {
            if (currentAppliance.id === 'sink') {
                mergedName = `${applianceType} Sink`;
            } else if (currentAppliance.id === 'microwave') {
                mergedName = `${applianceType} Microwave`;
            }
        }

        if (result && sizeType === 'product') {
            dimensions = {
                width: parseFloat(result.W),
                depth: parseFloat(result.D),
                height: parseFloat(result.H),
            };
            appliance = {
                ...currentAppliance,
                id: uniqueId,
                name: mergedName,
                dimensions,
                productId: result['product #'],
                sizeType: undefined,
                applianceType: applianceType,
                panelReady: isPanelReady,
            };
        } else {
            if (sizeType === 'standard') {
                dimensions = JSON.parse(standardSize);
            } else if (sizeType === 'custom') {
                dimensions = {
                    width: parseFloat(customSize.width),
                    depth: parseFloat(customSize.depth),
                    height: parseFloat(customSize.height),
                };
            }
            appliance = {
                ...currentAppliance,
                id: uniqueId,
                name: mergedName,
                dimensions,
                additionalInfo: customSize.additionalInfo,
                sizeType,
                applianceType: applianceType,
                panelReady: isPanelReady, // Add panelReady property
            };
        }

        // Update selectedAppliances
        setSelectedAppliances(prev => {
            if (selectedApplianceForOptions) {
                return prev.map(a => (a.id === selectedApplianceForOptions.id ? appliance : a));
            } else {
                return [...prev, appliance];
            }
        });

        closeDialog();
    };

    const closeDialog = () => {
        setIsDialogOpen(false);
        setCurrentAppliance(null);
        setSizeType('');
        setCustomSize({ width: '', depth: '', height: '', additionalInfo: '' });
        setSearchProduct('');
        setStandardSize('');
        setOtherParametrs(false);
        setNewAppliancesOther(false);
        setSearchResults([]);
        setApplianceType('');
        setIsPanelReady(false);
    };

    const isCustomSizeValid = () => {
        return customSize.width !== '' && customSize.depth !== '' && customSize.height !== '';
    };

    const isStandardSizeValid = () => {
        return standardSize !== '';
    };

    const handleSearchProduct = async () => {
        if (searchProduct) {
            setIsSearching(true);
            const newSearch = {
                appliance_type: currentAppliance.name.toLowerCase(),
                prompt: searchProduct,
            };
            try {
                const response = await api.post('/bedrock_invoke_lambda', newSearch);

                const data = response.data;

                if (data.answer && Array.isArray(data.answer)) {
                    setSearchResults(data.answer);
                }
            } catch (error) {
                console.error('Search failed:', error);
                setSearchResults([]);
            } finally {
                setIsSearching(false);
            }
        }
    };
    const handleSizeTypeChange = value => {
        setSizeType(value);
    };

    // const renderPanelReadyCheckbox = () => {
    //     if (!currentAppliance) return null;

    //     if (!['fridge', 'dishwasher'].includes(currentAppliance.id) && !currentAppliance.id?.startsWith('other_')) {
    //         return null;
    //     }

    //     return (
    //         <div className="flex items-center space-x-2 mt-4">
    //             <Checkbox
    //                 id="panelReady"
    //                 checked={isPanelReady}
    //                 onCheckedChange={checked => setIsPanelReady(checked)}
    //             />
    //             <Label htmlFor="panelReady">Panel Ready</Label>
    //         </div>
    //     );
    // };

    const renderPanelReadyCheckbox = () => {
        if (!currentAppliance) return null;
    
        // Find the appliance template in the config
        const applianceTemplate = applianceConfig.appliances.find(a => a.id === currentAppliance.id);
        
        // Show checkbox if it's either:
        // 1. A custom appliance (other_)
        // 2. An appliance that can be panel ready according to the config
        if (!currentAppliance.id?.startsWith('other_') && 
            (!applianceTemplate || !applianceTemplate.canBePanelReady)) {
            return null;
        }
    
        return (
            <div className="flex items-center space-x-2 mt-4">
                <Checkbox
                    id="panelReady"
                    checked={isPanelReady}
                    onCheckedChange={checked => setIsPanelReady(checked)}
                />
                <Label htmlFor="panelReady">Panel Ready</Label>
            </div>
        );
    };

    const handleSelectSearchResult = result => {
        handleSaveAppliance(result);
    };

    const handleRemoveAppliance = applianceId => {
        const baseId = applianceId.split('-')[0];
        const isCustomAppliance = !applianceConfig.appliances.find(a => a.id === baseId);

        setSelectedAppliances(prev => {
            const newSelectedAppliances = prev.filter(a => a.id !== applianceId);
            if (isCustomAppliance) {
                const remainingCount = newSelectedAppliances.filter(a => a.id.split('-')[0] === baseId).length;

                if (remainingCount === 0) {
                    setAllAppliances(currentAll => currentAll.filter(a => a.id !== baseId));
                }
            }
            const appliancesToSave = newSelectedAppliances.map(appliance => ({
                id: appliance.id,
                name: appliance.name,
                sizeInfo: appliance.sizeInfo,
                dimensions: appliance.dimensions && {
                    width: appliance.dimensions.width.toString(),
                    depth: appliance.dimensions.depth.toString(),
                    height: appliance.dimensions.height.toString(),
                },
                additionalInfo: appliance.additionalInfo,
                sizeType: appliance.sizeType,
                productId: appliance.productId,
                panel_Ready: appliance.panelReady,
            }));

            ProjectService.updateProject(projectId, {
                appliances: appliancesToSave,
            }).catch(error => {
                console.error('Failed to save appliances:', error);
            });

            return newSelectedAppliances;
        });
    };

    const renderSearchResults = () => {
        if (!searchResults.length) return null;

        return (
            <div className="mt-4">
                <h3 className="font-semibold mb-2">Search Results</h3>
                <div className="max-h-60 overflow-y-auto">
                    {searchResults.map((result, index) => (
                        <div key={index} className="flex items-center justify-between p-2 border-b hover:bg-gray-50">
                            <div className="flex-1">
                                <p className="font-medium">
                                    {result.brand} - {result['product #']}
                                </p>
                                <p className="text-sm text-gray-600">
                                    W: {result.W}" × D: {result.D}" × H: {result.H}"
                                </p>
                            </div>
                            <div className="flex gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => window.open(result.search_link, '_blank')}
                                >
                                    <FaExternalLinkAlt className="w-4 h-4" />
                                </Button>
                                <Button size="sm" onClick={() => handleSelectSearchResult(result)} className="px-6">
                                    Select
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const renderProductSearch = () => (
        <div className="space-y-4">
            <div>
                <Label htmlFor="searchProduct">Search {currentAppliance?.name || 'Product'}</Label>
                <div className="flex mt-1">
                    <Input
                        id="searchProduct"
                        value={searchProduct}
                        onChange={e => setSearchProduct(e.target.value)}
                        className="flex-grow"
                        placeholder="Enter Model, Brand or any description"
                    />
                    <Button onClick={handleSearchProduct} className="ml-2" disabled={isSearching}>
                        <FaSearch className="mr-2" />
                        {isSearching ? 'Searching...' : 'Search'}
                    </Button>
                </div>
            </div>
            {renderSearchResults()}
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-100 p-8">
            {flowId && (
                <ProjectHeader showBackButton={true} collectionData={collectionData} projectName={projectData?.name} />
            )}
            <main className="pt-20 px-4 text-center">
                {/* Render navigation only if project is part of a collection */}
                {projectData?.collectionId && flowId && (
                    <CollectionNavigation
                        // collectionId={projectData.collectionId}
                        collectionData={collectionData}
                        isLoading={iscollectionLoading}
                        currentProjectId={projectId}
                    />
                )}
                <h1 className="text-3xl font-bold mb-8">Select the appliances to incorporate in your design</h1>
                {isLoading ? (
                    <div className="flex justify-center items-center h-64">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                    </div>
                ) : (
                    <>
                        <Card className="max-w-6xl mx-auto mb-8">   
                            <CardContent className="pt-4 px-4 grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-4">
                                {allAppliances.map(appliance => {
                                    const count = selectedAppliances.filter(
                                        a => a.id === appliance.id || a.id.startsWith(`${appliance.id}-`)
                                    ).length;
                                    const isSelected = count > 0;

                                    return (
                                        <button
                                            key={appliance.id}
                                            onClick={() => handleApplianceClick(appliance)}
                                            className={`p-4 rounded-lg flex flex-col items-center justify-center transition-all hover:scale-105 relative ${
                                                isSelected
                                                    ? 'bg-green-500 text-white hover:bg-green-600'
                                                    : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                                            }`}
                                        >
                                            <img
                                                src={appliance.iconUrl}
                                                alt={appliance.name}
                                                className="w-8 h-8"
                                            />
                                            <span className="mt-2 text-sm">{appliance.name}</span>
                                            {count > 0 && (
                                                <span className="absolute top-1 right-1 bg-white text-green-600 rounded-full w-6 h-6 flex items-center justify-center text-sm font-medium">
                                                    {count}
                                                </span>
                                            )}
                                        </button>
                                    );
                                })}
                            </CardContent>
                        </Card>
                        <Card className="max-w-6xl mx-auto mb-8">
                            <CardHeader>
                                <CardTitle>Selected Appliances</CardTitle>
                            </CardHeader>
                            <CardContent className="pt-4 px-4 grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-4">
                                {selectedAppliances.map(appliance => (
                                    <div key={appliance.id} className="mb-4 p-4 bg-gray-100 rounded-lg">
                                        <h3 className="font-semibold">{appliance.name}</h3>

                                        {appliance.dimensions && (
                                            <div className="mt-2 space-y-1">
                                                <p>Width: {appliance.dimensions.width}"</p>
                                                <p>Depth: {appliance.dimensions.depth}"</p>
                                                <p>Height: {appliance.dimensions.height}"</p>
                                                {!appliance.sizeType && <p>Custom Product: {appliance.productId}</p>}
                                                {appliance.sizeType && <p>Size type: {appliance.sizeType}</p>}
                                                {appliance.additionalInfo && (
                                                    <p>Additional Info: {appliance.additionalInfo}</p>
                                                )}
                                                {appliance.productId && appliance.sizeType && (
                                                    <p>Product #: {appliance.productId}</p>
                                                )}
                                                {appliance.panelReady && <p>Panel Ready: Yes</p>}
                                            </div>
                                        )}
                                        <div className="flex items-center justify-between mt-4">
                                            <Button onClick={() => openDialog(appliance)} variant="secondary">
                                                Edit
                                            </Button>
                                            <Button
                                                onClick={() => handleRemoveAppliance(appliance.id)}
                                                variant="secondary"
                                                size="icon"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </CardContent>
                        </Card>
                        {/* <Button onClick={handleComplete}>Complete This Section</Button> */}
                    </>
                )}
                <ApplianceOptionsDialog
                    isOpen={isOptionsDialogOpen}
                    onClose={() => {
                        setIsOptionsDialogOpen(false);
                        setSelectedApplianceForOptions(null);
                    }}
                    onDelete={handleDeleteFromOptions}
                    onAddAnother={handleAddAnotherFromOptions}
                    applianceName={selectedApplianceForOptions?.name || ''}
                />
                <ApplianceDeleteDialog
                    isOpen={isDeleteDialogOpen}
                    onClose={() => {
                        setIsDeleteDialogOpen(false);
                        setCategoryToDelete(null);
                    }}
                    onDelete={applianceId => {
                        handleRemoveAppliance(applianceId);
                        setIsDeleteDialogOpen(false);
                        setCategoryToDelete(null);
                    }}
                    onDeleteAll={() => {
                        if (categoryToDelete) {
                            const baseId = categoryToDelete.baseId;

                            setSelectedAppliances(prev => {
                                const newSelectedAppliances = prev.filter(a => !a.id.startsWith(baseId));
                                const isCustomAppliance = !applianceConfig.appliances.find(a => a.id === baseId);

                                if (isCustomAppliance) {
                                    setAllAppliances(prevAll => prevAll.filter(a => a.id !== baseId));
                                }

                                return newSelectedAppliances;
                            });
                        }
                        setIsDeleteDialogOpen(false);
                        setCategoryToDelete(null);
                    }}
                    appliances={categoryToDelete?.appliances || []}
                    categoryName={categoryToDelete?.name || ''}
                />
            </main>
            {flowId ? (
                <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 flex justify-end items-center shadow-lg">
                    <Button onClick={handleComplete}>Complete This Section</Button>
                </div>
            ) : (
                <div className="flex justify-end mt-4">
                    <Button onClick={handleComplete}>Save Changes</Button>
                </div>
            )}
            <Dialog open={isDialogOpen && newAppliancesOther} onOpenChange={setIsDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Enter Appliance Type</DialogTitle>
                    </DialogHeader>
                    <div>
                        <Input
                            id="newOtherAppliances"
                            value={customName}
                            onChange={e => setCustomName(e.target.value)}
                        />
                    </div>
                    <DialogFooter>
                        <Button onClick={closeDialog} variant="secondary">
                            Cancel
                        </Button>
                        <Button onClick={handleOtherApplianceSave} disabled={!customName.trim()}>
                            Continue
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            <Dialog open={isDialogOpen && !newAppliancesOther} onOpenChange={setIsDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{currentAppliance?.name}</DialogTitle>
                    </DialogHeader>

                    {(currentAppliance?.id === 'sink' || currentAppliance?.id === 'microwave') && (
                        <div className="space-y-4 mb-4">
                            <Label>Select Type</Label>
                            <select
                                value={applianceType}
                                onChange={e => setApplianceType(e.target.value)}
                                className="w-full p-2 border rounded-md"
                                required
                            >
                                <option value="">Select {currentAppliance.name} Type</option>
                                {/* {applianceConfig.applianceTypes[currentAppliance.id].map(type => ( */}
                                {(applianceConfig.appliances.find(app => app.id === currentAppliance.id)?.applianceTypes || []).map(type => (
                                    <option key={type} value={type}>
                                        {type}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {(applianceType ||
                        (!currentAppliance?.id.includes('sink') && !currentAppliance?.id.includes('microwave'))) && (
                        <>
                            <RadioGroup value={sizeType} onValueChange={handleSizeTypeChange}>
                                {!otherParametrs && (
                                    <div className="flex items-center space-x-2">
                                        <RadioGroupItem value="standard" id="standard" />
                                        <Label htmlFor="standard">Standard Size</Label>
                                    </div>
                                )}
                                <div className="flex items-center space-x-2">
                                    <RadioGroupItem value="custom" id="custom" />
                                    <Label htmlFor="custom">Custom Size</Label>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <RadioGroupItem value="product" id="product" />
                                    <Label htmlFor="product">Search Product</Label>
                                </div>
                            </RadioGroup>
                            {(sizeType === 'standard' || sizeType === 'custom') && renderPanelReadyCheckbox()}
                            {sizeType === 'standard' && !otherParametrs && (
                                <div className="space-y-4">
                                    <Label>Select Standard Size</Label>
                                    <select
                                        value={standardSize}
                                        onChange={handleChangeSize}
                                        className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                    >
                                        {/* {applianceConfig.standardSizes[currentAppliance?.id]?.map((size, index) => ( */}
                                        {(applianceConfig.appliances.find(app => app.id === currentAppliance.id)?.standardSizes || []).map((size, index) => (
                                            <option key={index} value={JSON.stringify(size)}>
                                                {`W: ${size.width}" × D: ${size.depth}" × H: ${size.height}"`}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {sizeType === 'custom' && (
                                <div className="space-y-4">
                                    <div>
                                        <Label htmlFor="width">Width (inches)</Label>
                                        <Input
                                            id="width"
                                            value={customSize.width}
                                            onChange={e => setCustomSize({ ...customSize, width: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="depth">Depth (inches)</Label>
                                        <Input
                                            id="depth"
                                            value={customSize.depth}
                                            onChange={e => setCustomSize({ ...customSize, depth: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="height">Height (inches)</Label>
                                        <Input
                                            id="height"
                                            value={customSize.height}
                                            onChange={e => setCustomSize({ ...customSize, height: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="additionalInfo">Additional Info</Label>
                                        <Input
                                            id="additionalInfo"
                                            value={customSize.additionalInfo}
                                            onChange={e =>
                                                setCustomSize({ ...customSize, additionalInfo: e.target.value })
                                            }
                                        />
                                    </div>
                                </div>
                            )}

                            {sizeType === 'product' && renderProductSearch()}
                        </>
                    )}

                    <DialogFooter>
                        <Button onClick={closeDialog} variant="secondary">
                            Cancel
                        </Button>
                        {sizeType !== 'product' && (
                            <Button
                                onClick={handleSaveAppliance}
                                disabled={
                                    (sizeType === 'custom' && !isCustomSizeValid()) ||
                                    (sizeType === 'standard' && !isStandardSizeValid()) ||
                                    ((currentAppliance?.id === 'sink' || currentAppliance?.id === 'microwave') &&
                                        !applianceType)
                                }
                            >
                                Save
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default ApplianceSelector;
